import { create } from 'zustand';
import { useExperienceStore } from './experienceStore';
import type { DomainConfigData } from '../types/domainConfig';

/**
 * Module visibility — replaces the Angular `ModuleGuard`. The Tenant API's
 * domain config returns `moduleConfig` (a record of module keys to their
 * enabled flag and per-module settings); features check this store before
 * mounting routes or rendering tiles.
 */

const STORAGE_KEY = 'paas_domain_config';
const BASE_CONFIG_KEY = 'paas_domain_config_base';

interface ModuleState {
  config: DomainConfigData | null;
  /** The hub/base domain config — cached so it restores instantly on sign-out (no flash). */
  baseConfig: DomainConfigData | null;
  /** Copied from `user.license.enabled_modules` on login/hydrate so UI selectors re-render. */
  licensedModules: string[];

  setConfig: (config: DomainConfigData | null) => void;
  /** Persist the hub/base domain config (called when the hub domain resolves). */
  setBaseConfig: (config: DomainConfigData | null) => void;

  /** Keep in sync with auth — do not read authStore inside `isModuleEnabled` (no reactivity). */
  syncLicensedModules: (modules: string[]) => void;

  /**
   * Module enabled? `moduleConfig[moduleKey]` may be a boolean, an object
   * with `enabled`, or absent. Mirrors the Angular service's permissive read.
   */
  isModuleEnabled: (moduleKey: string) => boolean;

  /** Pull a typed setting nested under a module's config block. */
  getModuleSetting: <T = unknown>(moduleKey: string, settingKey: string) => T | undefined;

  /** Convenience: list of all enabled module keys (for tile-grid filtering). */
  getEnabledModules: () => string[];

  /**
   * Welcome + auth screen imagery, sourced from the domain config's
   * `moduleConfig.portal.*` / `moduleConfig.onboarding.*` settings (the
   * per-page auth background images configured against the tenant's
   * branding). Any field may be undefined; `useBrandImagery()` then falls
   * back to bundled local assets.
   */
  getDomainImagery: () => {
    welcomeHero?: string;
    loginBackground?: string;
    signupBackground?: string;
    forgotBackground?: string;
  };

  isUnauthenticatedFunctionAllowed: (fn: string) => boolean;

  hydrate: () => void;
}

const CORE_MODULES = new Set(['users', 'roles', 'settings', 'admin', 'system']);

function readEnabled(value: unknown): boolean {
  if (value === true) return true;
  if (value && typeof value === 'object' && (value as { enabled?: boolean }).enabled === true) {
    return true;
  }
  return false;
}

/** Tenant license — same rules as Angular `ModuleService.isModuleEnabled`. */
function isLicensedModule(enabledModules: string[], moduleKey: string): boolean {
  if (enabledModules.includes(moduleKey)) return true;
  if (moduleKey.includes(':')) {
    const parent = moduleKey.split(':')[0]!;
    if (enabledModules.includes(parent)) return true;
  }
  return false;
}

/**
 * When `user.license` is missing (stale cache), infer from compiled shell items
 * the backend already filtered by license.
 */
function isModuleInCompiledExperience(moduleKey: string): boolean {
  const exp = useExperienceStore.getState().experience;
  if (!exp) return false;

  const matches = (moduleRequired?: string, moduleKeyOnItem?: string) =>
    moduleRequired === moduleKey || moduleKeyOnItem === moduleKey;

  const nav = [
    ...(exp.navigation?.hamburger ?? []),
    ...(exp.navigation?.bottom_tabs ?? []),
  ];
  if (nav.some((item) => matches(item.module_required, item.module_key))) return true;
  if ((exp.tiles?.main ?? []).some((t) => matches(t.module_required))) return true;
  for (const cat of exp.tiles?.more_categories ?? []) {
    if ((cat.tiles ?? []).some((t) => matches(t.module_required))) return true;
  }
  return false;
}

export const useModuleStore = create<ModuleState>((set, get) => ({
  config: null,
  baseConfig: null,
  licensedModules: [],

  syncLicensedModules(modules) {
    set({ licensedModules: modules });
  },

  setConfig(config) {
    set({ config });
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.warn('[modules] cache clear failed:', error);
    }
  },

  setBaseConfig(config) {
    set({ baseConfig: config });
    try {
      if (config) localStorage.setItem(BASE_CONFIG_KEY, JSON.stringify(config));
      else localStorage.removeItem(BASE_CONFIG_KEY);
    } catch (error) {
      console.warn('[modules] base config persist failed:', error);
    }
  },

  isModuleEnabled(moduleKey) {
    if (CORE_MODULES.has(moduleKey)) return true;

    const licensed = get().licensedModules;
    if (licensed.length > 0) {
      return isLicensedModule(licensed, moduleKey);
    }

    // License not on cached user — trust compiled nav/tiles (server-side gated).
    if (isModuleInCompiledExperience(moduleKey)) return true;

    const config = get().config;
    if (!config) return false;
    const entry = config.moduleConfig?.[moduleKey];
    return readEnabled(entry);
  },

  getModuleSetting<T>(moduleKey: string, settingKey: string): T | undefined {
    const entry = get().config?.moduleConfig?.[moduleKey];
    if (!entry || typeof entry !== 'object') return undefined;
    return (entry as Record<string, unknown>)[settingKey] as T | undefined;
  },

  getEnabledModules() {
    const config = get().config;
    if (!config?.moduleConfig) return [];
    return Object.entries(config.moduleConfig)
      .filter(([, value]) => readEnabled(value))
      .map(([key]) => key);
  },

  getDomainImagery() {
    const mc = get().config?.moduleConfig as Record<string, unknown> | undefined;
    const portal = (mc?.portal ?? {}) as Record<string, unknown>;
    const onboarding = (mc?.onboarding ?? {}) as Record<string, unknown>;
    const str = (v: unknown): string | undefined =>
      typeof v === 'string' && v.trim().length > 0 ? v : undefined;
    return {
      welcomeHero: str(onboarding.welcome_background_image_url),
      loginBackground: str(portal.auth_login_background_image_url),
      signupBackground: str(portal.auth_signup_background_image_url),
      forgotBackground: str(portal.auth_forgot_password_background_image_url),
    };
  },

  isUnauthenticatedFunctionAllowed(fn) {
    return !!get().config?.unauthenticatedFunctions?.includes(fn);
  },

  hydrate() {
    if (typeof window === 'undefined') return;
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.warn('[modules] cache clear failed:', error);
    }
    // Restore the cached hub/base config so sign-out can apply it instantly.
    try {
      const raw = localStorage.getItem(BASE_CONFIG_KEY);
      if (raw) set({ baseConfig: JSON.parse(raw) as DomainConfigData });
    } catch {
      /* corrupt cache — ignore */
    }
  },
}));
